import { t as uiText } from '../../i18n';
import { collection, doc, getDocs, getDoc, serverTimestamp, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { HashtagData, BoardWithHashtags } from '../../types/hashtag';
import { normalizeForSearch } from '../collaboration/normalizationUtils';



// テキストからハッシュタグを抽出する関数
export const extractHashtags = (text: string): string[] => {
  // 行の先頭または空白の後の#のみにマッチする正規表現
  const hashtagRegex = /(?:^|\s)#([a-zA-Z0-9_\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]+)/g;
  const matches = [];
  let match;

  // 正規表現をリセットしてから実行
  hashtagRegex.lastIndex = 0;

  while ((match = hashtagRegex.exec(text)) !== null) {
    matches.push(match[1]); // キャプチャグループ（#を除いた部分）を取得
  }

  if (!matches.length) {
    return [];
  }

  const uniqueHashtags = Array.from(new Set(matches));

  return uniqueHashtags;
};

// ハッシュタグ名をFirestoreドキュメントIDに適した形式に変換する関数
// カタカナ・ひらがなは分けて管理するため、正規化は行わない
const normalizeTagName = (tagName: string): string => {
  return tagName
    .replace(/[\/\[\]]/g, '_') // Firestore禁止文字をエスケープ
    .replace(/\s+/g, '_') // スペースをアンダースコアに
    .substring(0, 1500); // FirestoreドキュメントIDの長さ制限
};

// 一意のハッシュタグIDを生成する関数（後方互換性のため残す）
const generateHashtagId = (): string => {
  return `hashtag_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// previewData.content配列からテキストを抽出する関数（改行対応版）
const extractTextFromPreviewContent = (contentArray: any[]): string => {
  if (!Array.isArray(contentArray)) return '';
  
  try {
    const textParts: string[] = [];
    
    contentArray.forEach((item, index) => {
      if (item && typeof item === 'object') {
        // fileAttachmentタイプの場合はファイル名を表示
        if (item.type === 'fileAttachment') {
          if (item.attrs && item.attrs.title) {
            textParts.push(`📎 ${item.attrs.title}`);
          } else {
            textParts.push(uiText("📎 Attachments"));
          }
        }
        // paragraphタイプの場合は改行を追加
        else if (item.type === 'paragraph') {
          if (item.content && Array.isArray(item.content)) {
            const paragraphText = item.content
              .map((contentItem: any) => {
                if (contentItem && typeof contentItem === 'object') {
                  if (contentItem.type === 'text' && contentItem.text) {
                    return contentItem.text;
                  }
                  if (contentItem.type === 'autoLink' && contentItem.attrs?.linkText) {
                    return contentItem.attrs.linkText;
                  }
                  if (contentItem.type === 'hardBreak') {
                    return '\n';
                  }
                }
                return '';
              })
              .join('');
            
            // 段落の内容を追加（改行は後で処理）
            if (paragraphText.trim()) {
              textParts.push(paragraphText);
            }
          }
          
          // paragraphタイプの場合は必ず改行を追加（空の段落も含む）
          // ただし、最後の段落の場合は改行を追加しない
          if (index < contentArray.length - 1) {
            textParts.push('\n');
          }
        }
        // その他のタイプも処理
        else if (item.content && Array.isArray(item.content)) {
          const itemText = extractTextFromPreviewContent(item.content);
          if (itemText) {
            textParts.push(itemText);
          }
        }
      }
    });
    
    return textParts.join('').trim();
  } catch (error) {
    console.error('previewData.contentからのテキスト抽出エラー:', error);
    return '';
  }
};

// ハッシュタグをワークスペースに保存する関数（個別更新方式）
export const saveHashtags = async (
  workspaceId: string, 
  boardId: string, 
  hashtags: string[]
): Promise<void> => {
  if (!hashtags.length) {
    return;
  }

  try {
    // 各ハッシュタグを個別に更新（トランザクションを使わない）
    for (const hashtagName of hashtags) {
      // ドキュメントIDは元の表記を使用（Firestore禁止文字のみエスケープ）
      const documentId = normalizeTagName(hashtagName);
      // 検索用の正規化版も生成
      const normalizedForSearch = normalizeForSearch(hashtagName);
      const hashtagRef = doc(db, `workspaces/${workspaceId}/hashtags/${documentId}`);

      try {
        // 既存のハッシュタグを確認
        const hashtagDoc = await getDoc(hashtagRef);

        if (hashtagDoc.exists()) {
          // 既存のハッシュタグを更新
          const existingData = hashtagDoc.data();
          const updatedBoards = existingData.boards.includes(boardId) 
            ? existingData.boards 
            : [...existingData.boards, boardId];

          await updateDoc(hashtagRef, {
            count: updatedBoards.length,
            boards: updatedBoards,
            lastUsed: serverTimestamp(),
          });
        } else {
          // 新規ハッシュタグを作成
          const newHashtagData = {
            name: hashtagName, // 元の表記を保持
            normalizedName: normalizedForSearch, // 検索用の正規化された名前を保存
            count: 1,
            boards: [boardId],
            lastUsed: serverTimestamp(),
            createdAt: serverTimestamp(),
          };

          await setDoc(hashtagRef, newHashtagData);
        }
      } catch (individualError) {
        console.error(`❌ 個別ハッシュタグ処理エラー (${hashtagName}):`, individualError);
        // 個別のエラーでも処理を継続
      }
    }
  } catch (error) {
    console.error('❌ saveHashtags全体エラー:', error);
    if (error instanceof Error) {
      console.error('❌ エラー詳細:', {
        message: error.message,
        code: (error as any).code,
        stack: error.stack
      });
    }
    throw error;
  }
};

// ハッシュタグをワークスペースから削除する関数（個別更新方式）
export const removeHashtags = async (
  workspaceId: string, 
  boardId: string, 
  hashtags: string[]
): Promise<void> => {
  if (!hashtags.length) return;

  try {
    // 各ハッシュタグを個別に更新（トランザクションを使わない）
    for (const hashtagName of hashtags) {
      // ドキュメントIDは元の表記を使用（Firestore禁止文字のみエスケープ）
      const documentId = normalizeTagName(hashtagName);
      const hashtagRef = doc(db, `workspaces/${workspaceId}/hashtags/${documentId}`);
      
      try {
        // 既存のハッシュタグを確認
        const hashtagDoc = await getDoc(hashtagRef);
        
        if (hashtagDoc.exists()) {
          const existingData = hashtagDoc.data() as HashtagData;
          const updatedBoards = existingData.boards.filter(id => id !== boardId);
          
          if (updatedBoards.length === 0) {
            // ボードが0になったらハッシュタグを削除
            await deleteDoc(hashtagRef);
          } else {
            // ボード数を更新
            await updateDoc(hashtagRef, {
              count: updatedBoards.length,
              boards: updatedBoards,
            });
          }
        } else {
          console.warn(`⚠️ removeHashtags: ハッシュタグが見つかりません: ${hashtagName}`);
        }
      } catch (individualError) {
        console.error(`❌ 個別ハッシュタグ削除エラー (${hashtagName}):`, individualError);
        // 個別のエラーでも処理を継続
      }
    }
  } catch (error) {
    console.error('❌ removeHashtags全体エラー:', error);
    if (error instanceof Error) {
      console.error('❌ エラー詳細:', {
        message: error.message,
        code: (error as any).code,
        stack: error.stack
      });
    }
    throw error;
  }
};

// ワークスペースの全ハッシュタグを取得する関数
export const getWorkspaceHashtags = async (workspaceId: string): Promise<HashtagData[]> => {
  const hashtagsRef = collection(db, `workspaces/${workspaceId}/hashtags`);

  try {
    const snapshot = await getDocs(hashtagsRef);

    const hashtags = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data
      } as HashtagData;
    });

    // 各ハッシュタグについて、アーカイブされていないボードのみをカウント
    const updatedHashtags = await Promise.all(
      hashtags.map(async (hashtag) => {
        const boardsRef = collection(db, `workspaces/${workspaceId}/boards`);

        // ハッシュタグに関連するボードを取得
        const boardDocs = await Promise.all(
          hashtag.boards.map(boardId => getDoc(doc(boardsRef, boardId)))
        );

        // アーカイブされていないボードのみをフィルタリング
        const activeBoards = boardDocs
          .filter(doc => doc.exists())
          .filter(doc => !doc.data().archived);

        const result = {
          ...hashtag,
          count: activeBoards.length,
          boards: activeBoards.map(doc => doc.id)
        };

        return result;
      })
    );

    return updatedHashtags;
  } catch (error) {
    console.error('❌ getWorkspaceHashtagsエラー:', error);
    throw error;
  }
};

// 特定のハッシュタグが使われているボードを取得する関数
export const getBoardsByHashtag = async (
  workspaceId: string, 
  hashtagName: string
): Promise<BoardWithHashtags[]> => {
  try {
    // ハッシュタグ名からIDを検索
    const hashtagsRef = collection(db, `workspaces/${workspaceId}/hashtags`);
    const snapshot = await getDocs(hashtagsRef);

    let hashtagId: string | null = null;
    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.name === hashtagName) {
        hashtagId = doc.id;
        break;
      }
    }

    if (!hashtagId) {
      return [];
    }

    const hashtagRef = doc(db, `workspaces/${workspaceId}/hashtags/${hashtagId}`);

    const hashtagDoc = await getDoc(hashtagRef);

    if (!hashtagDoc.exists()) {
      return [];
    }

    const hashtagData = hashtagDoc.data() as HashtagData;

    const boardsRef = collection(db, `workspaces/${workspaceId}/boards`);

    // ハッシュタグが使われているボードを取得
    const boardDocs = await Promise.all(
      hashtagData.boards.map(boardId => 
        getDoc(doc(boardsRef, boardId))
      )
    );

    const boards = boardDocs
      .filter(doc => doc.exists())
      .map(doc => {
        const data = doc.data();
        
        // previewData.contentがある場合は、そこからテキストを抽出
        let extractedContent = '';
        if (data.previewData && data.previewData.content) {
          extractedContent = extractTextFromPreviewContent(data.previewData.content);
        } else if (data.searchData?.originalContent) {
          // previewDataがない場合はsearchData.originalContentを使用
          extractedContent = data.searchData.originalContent;
        }

        // タイトルの取得優先順位: previewData.title > editData.title > title
        let boardTitle = '';
        if (data.previewData?.title) {
          boardTitle = data.previewData.title;
        } else if (data.editData?.title) {
          boardTitle = data.editData.title;
        } else {
          boardTitle = data.title || '';
        }

        // デバッグ用：タイトルが取得できない場合の詳細ログ
        if (!boardTitle) {
          console.warn('⚠️ getBoardsByHashtag: タイトルが取得できません', {
            boardId: doc.id,
            hasPreviewData: !!data.previewData,
            previewDataTitle: data.previewData?.title,
            hasEditData: !!data.editData,
            editDataTitle: data.editData?.title,
            title: data.title,
            dataKeys: Object.keys(data)
          });
        }

        return {
          id: doc.id,
          ...data,
          title: boardTitle, // 明示的にタイトルを設定
          content: extractedContent, // 抽出したコンテンツを設定
          pinned: data.pinned || false
        } as BoardWithHashtags;
      })
      // アーカイブされていないボードのみをフィルタリング
      .filter(board => !board.archived);

    return boards;
  } catch (error) {
    console.error('❌ getBoardsByHashtagエラー:', error);
    throw error;
  }
};

// ボードからハッシュタグを取得する関数
export const getBoardHashtags = async (
  workspaceId: string, 
  boardId: string
): Promise<string[]> => {
  try {
    // ボードドキュメントを取得
    const boardRef = doc(db, `workspaces/${workspaceId}/boards/${boardId}`);
    const boardDoc = await getDoc(boardRef);

    if (!boardDoc.exists()) {
      return [];
    }

    const boardData = boardDoc.data();

    // 新しいDB構造: searchData.hashtagsから直接取得
    const hashtags = boardData.searchData?.hashtags || [];

    return hashtags;
  } catch (error) {
    console.error('❌ getBoardHashtagsエラー:', error);
    throw error;
  }
};

// アーカイブされたボードのハッシュタグを管理する関数
export const handleArchivedBoardHashtags = async (
  workspaceId: string, 
  boardId: string, 
  isArchiving: boolean
): Promise<void> => {
  try {
    // ボードからハッシュタグを取得
    const hashtags = await getBoardHashtags(workspaceId, boardId);

    if (hashtags.length === 0) {
      return;
    }

    if (isArchiving) {
      // アーカイブ時：ハッシュタグのカウントを減算
      // 既存のハッシュタグを事前に取得
      const hashtagNameToIdMap = new Map<string, string>();
      const hashtagsRef = collection(db, `workspaces/${workspaceId}/hashtags`);
      const snapshot = await getDocs(hashtagsRef);
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.name) {
          hashtagNameToIdMap.set(data.name, doc.id);
        }
      });
      
      // 各ハッシュタグを個別に処理（トランザクションを使わない）
      for (const hashtagName of hashtags) {
        try {
          // ハッシュタグ名からIDを検索
          const hashtagId = hashtagNameToIdMap.get(hashtagName);
          
          if (!hashtagId) {
            continue;
          }
          
          const hashtagRef = doc(db, `workspaces/${workspaceId}/hashtags/${hashtagId}`);
          const hashtagDoc = await getDoc(hashtagRef);
          
          if (hashtagDoc.exists()) {
            const existingData = hashtagDoc.data() as HashtagData;
            const updatedBoards = existingData.boards.filter(id => id !== boardId);
            
            if (updatedBoards.length === 0) {
              // ボードが0になったらハッシュタグを削除
              await deleteDoc(hashtagRef);
            } else {
              // ボード数を更新
              await updateDoc(hashtagRef, {
                count: updatedBoards.length,
                boards: updatedBoards,
              });
            }
          }
        } catch (individualError) {
          console.error(`❌ 個別アーカイブ時ハッシュタグ処理エラー (${hashtagName}):`, individualError);
          // 個別のエラーでも処理を継続
        }
      }
    } else {
      // 復元時：ハッシュタグのカウントを加算
      await saveHashtags(workspaceId, boardId, hashtags);
    }
  } catch (error) {
    console.error('❌ handleArchivedBoardHashtagsエラー:', error);
    throw error;
  }
}; 

// 既存のハッシュタグデータを新しい形式に移行する関数
export const migrateHashtagsToNewFormat = async (workspaceId: string): Promise<void> => {
  try {
    const hashtagsRef = collection(db, `workspaces/${workspaceId}/hashtags`);
    const snapshot = await getDocs(hashtagsRef);

    // 各ハッシュタグを個別に処理（トランザクションを使わない）
    for (const docSnapshot of snapshot.docs) {
      try {
        const data = docSnapshot.data();
        const currentId = docSnapshot.id;

        // 既に新しい形式（nameフィールドがある）の場合はスキップ
        if (data.name) {
          continue;
        }

        // 古い形式の場合：ハッシュタグ名がドキュメントIDになっている
        const hashtagName = currentId;
        const newHashtagId = generateHashtagId();

        // 新しいIDでドキュメントを作成
        const newHashtagRef = doc(db, `workspaces/${workspaceId}/hashtags/${newHashtagId}`);
        await setDoc(newHashtagRef, {
          name: hashtagName,
          count: data.count || 0,
          boards: data.boards || [],
          lastUsed: data.lastUsed || serverTimestamp(),
          createdAt: data.createdAt || serverTimestamp(),
        });

        // 古いドキュメントを削除
        const oldHashtagRef = doc(db, `workspaces/${workspaceId}/hashtags/${currentId}`);
        await deleteDoc(oldHashtagRef);
      } catch (individualError) {
        console.error(`❌ 個別ハッシュタグマイグレーションエラー (${docSnapshot.id}):`, individualError);
        // 個別のエラーでも処理を継続
      }
    }
  } catch (error) {
    console.error('❌ ハッシュタグマイグレーション全体エラー:', error);
    throw error;
  }
}; 